import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import API from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { UsersThree, MapPin, CheckCircle, Clock, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");

/* Supplier picks which of its own people attend a job it holds. `onSaved(crew)` fires with the new crew list. */
export function SupplierCrewDialog({ open, onClose, entityType, entityId, reference, onSaved }) {
  const [team, setTeam] = useState([]);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  const slug = entityType === "emergency_job" ? "emergency_job" : "job";

  useEffect(() => {
    if (!open) return;
    API.get(`/supplier-portal/${slug}/${entityId}/crew`)
      .then(({ data }) => { setTeam(data.team || []); setPicked((data.crew || []).map((c) => c.user_id)); })
      .catch((e) => toast.error(e.response?.data?.detail || "Couldn't load your team"));
  }, [open, slug, entityId]);

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await API.put(`/supplier-portal/${slug}/${entityId}/crew`, { operative_ids: picked });
      toast.success(picked.length ? `${picked.length} assigned to ${reference || "this job"}${data.added ? " — they've been notified" : ""}` : "Crew cleared");
      onSaved?.(data.crew);
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't assign crew"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="supplier-crew-dialog">
        <DialogHeader><DialogTitle>Assign my team{reference ? ` — ${reference}` : ""}</DialogTitle></DialogHeader>
        {team.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="supplier-crew-empty">No one to assign yet. Add engineers under <strong>Team</strong> in your portal first — or tick yourself below.</p>
        ) : null}
        <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="supplier-crew-options">
          {team.map((m) => (
            <li key={m.id}>
              <label className={`flex items-center gap-3 rounded-md border p-2.5 cursor-pointer ${picked.includes(m.id) ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}>
                <input type="checkbox" checked={picked.includes(m.id)} onChange={() => toggle(m.id)} data-testid={`supplier-crew-option-${m.id}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium truncate">{m.name}{m.is_owner && <span className="ml-1.5 text-[10px] text-muted-foreground font-normal">(me)</span>}</p>
                  {m.trade && <p className="text-[11px] text-muted-foreground">{m.trade}</p>}
                </div>
              </label>
            </li>
          ))}
        </ul>
        <p className="text-[11px] text-muted-foreground">Assigned people see this job in their field portal with directions, arrive-on-site, photos and job sheets. Newly added people get a notification and email.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="supplier-crew-save">{busy ? "Saving…" : "Save crew"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* Card on the supplier's job page: who's attending, their live check-in status, and the assign button. */
export default function SupplierCrewCard({ entityType, entityId, reference, crew: initialCrew, userId, className = "" }) {
  const [crew, setCrew] = useState(initialCrew || []);
  const [open, setOpen] = useState(false);
  const slug = entityType === "emergency_job" ? "emergency_job" : "job";
  useEffect(() => { setCrew(initialCrew || []); }, [initialCrew]);
  useEffect(() => {
    API.get(`/supplier-portal/${slug}/${entityId}/crew`).then(({ data }) => setCrew(data.crew || [])).catch(() => {});
  }, [slug, entityId]);
  const mine = userId && crew.some((c) => c.user_id === userId);
  const fieldPath = `/my-work/${entityType === "emergency_job" ? "emergency" : "jobs"}/${entityId}`;
  return (
    <div className={`bg-card border border-border rounded-lg p-4 ${className}`} data-testid="supplier-crew-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h3 className="text-sm font-semibold flex items-center gap-1.5"><UsersThree size={16} className="text-primary" /> My team on this job</h3>
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="supplier-crew-assign-btn">{crew.length ? "Change" : "Assign my team"}</Button>
      </div>
      {crew.length === 0 ? (
        <p className="text-xs text-muted-foreground" data-testid="supplier-crew-none">Nobody assigned yet — assign an engineer (or yourself) so they get directions, geo-fenced arrive/complete, photo upload and job sheets in the field portal.</p>
      ) : (
        <ul className="space-y-1" data-testid="supplier-crew-list">
          {crew.map((c) => (
            <li key={c.user_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`supplier-crew-member-${c.user_id}`}>
              <span className="font-medium">{c.name}{c.user_id === userId && <span className="text-muted-foreground font-normal"> (me)</span>}</span>
              {c.completed_at ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle size={12} weight="fill" /> Completed {fmt(c.completed_at)}</span>
                : c.arrived_at ? <span className={`inline-flex items-center gap-1 ${c.arrival_off_site ? "text-amber-700" : "text-blue-700"}`}><MapPin size={12} weight="fill" /> On site {fmt(c.arrived_at)}{c.arrival_off_site ? " · off-site" : ""}</span>
                : <span className="inline-flex items-center gap-1 text-muted-foreground"><Clock size={12} /> Not arrived</span>}
            </li>
          ))}
        </ul>
      )}
      {mine && (
        <Link to={fieldPath} className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline" data-testid="supplier-crew-field-view-link">
          <ArrowSquareOut size={12} /> Open field view (directions, arrive, photos, job sheets)
        </Link>
      )}
      <SupplierCrewDialog open={open} onClose={() => setOpen(false)} entityType={entityType} entityId={entityId} reference={reference} onSaved={setCrew} />
    </div>
  );
}

/* Admin-side read-only line: which of the supplier's people are on the job and whether they've checked in. */
export function SupplierCrewSummary({ job, className = "" }) {
  const ids = job?.vendor_crew_ids || [];
  if (!ids.length) return null;
  const names = job.vendor_crew_names || [];
  const att = job.crew_attendance || {};
  return (
    <div className={`text-[11px] text-foreground/80 inline-flex flex-wrap items-center gap-x-2 gap-y-1 ${className}`} data-testid="supplier-crew-summary">
      <span className="inline-flex items-center gap-1"><UsersThree size={11} className="text-primary" /> {job.assigned_vendor_name || "Supplier"} crew:</span>
      {ids.map((id, i) => {
        const a = att[id] || {};
        const tone = a.completed_at ? "bg-emerald-500/10 text-emerald-700" : a.arrived_at ? (a.arrival_off_site ? "bg-amber-500/10 text-amber-700" : "bg-blue-500/10 text-blue-700") : "bg-muted text-muted-foreground";
        const label = a.completed_at ? `done ${fmt(a.completed_at)}` : a.arrived_at ? `on site ${fmt(a.arrived_at)}${a.arrival_off_site ? " · off-site" : ""}` : "not arrived";
        return <span key={id} className={`px-1.5 py-0.5 rounded ${tone}`} data-testid={`supplier-crew-chip-${id}`}><strong>{names[i] || "—"}</strong> · {label}</span>;
      })}
    </div>
  );
}
